import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { UserContext } from "../context/userContext";
import { DeviceContext } from "../context/deviceContext.js";
import PriceProp from "../props/priceProp.js";
import PropertyAddressProp from "../props/addressProp";
import PropertyDescriptionProp from "../props/descProp.js";
import Input from "../props/inputProp";
import { getData } from "../fetchRoutes/getData.js";
import { postData } from "../fetchRoutes/postData.js";
import {
    FacebookShareButton,
    TwitterShareButton,
    WhatsappShareButton,
    LinkedinShareButton,
    FacebookIcon,
    TwitterIcon,
    WhatsappIcon,
    LinkedinIcon,
} from "react-share";

export default function PlaceBidWithPropertyDetail() {
    const getOneProperty = process.env.REACT_APP_SAVE_PROPERTY;
    const getAnAuction = process.env.REACT_APP_GET_AN_AUCTION;
    const placeBid = process.env.REACT_APP_POST_BID;
    const getBids = process.env.REACT_APP_GET_BIDS;

    const { id } = useParams();
    const { isLoggedIn, userDetails } = useContext(UserContext);
    const { isMobile } = useContext(DeviceContext);
    const navigate = useNavigate();
    const location = useLocation();

    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [property, setProperty] = useState(null);
    const [isPropertyFetched, setPropertyFetched] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [thumbnails, setThumbnails] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [mainImage, setMainImage] = useState(null);
    const [auction, setAuction] = useState(null);
    const [isAuction, setIsAuction] = useState(false);
    const [bid, setBidAmount] = useState({ bidAmount: '' });
    const [previousBids, setPreviousBids] = useState({});
    const [showScrollArrow, setShowScrollArrow] = useState(true);
    const [touchStartX, setTouchStartX] = useState(null);
    const [touchEndX, setTouchEndX] = useState(null);

    const auctionRef = useRef(null);
    const shareUrl = window.location.href;
    const shareTitle = property?.title || "Check out this property!";

    useEffect(() => {
        setIsLoading(true);
        const fetchProperty = async () => {
            try {
                const url = `${getOneProperty}/${id}`;
                const response = await getData(url);
                if (response.status === "Success") {
                    setProperty(response.data);
                    setPropertyFetched(true);
                    if (response.data.images?.length) {
                        setMainImage(response.data.images[0]);
                        setThumbnails(response.data.images.slice(1, 5));
                    }
                }
            } catch (error) {
                console.log("Error retrieving property details");
            } finally {
                setIsLoading(false);
            }
        };
        fetchProperty();
    }, [getOneProperty, id]);

    useEffect(() => {
        if (isPropertyFetched) {
            setIsAuction(false);
            const fetchAuction = async () => {
                try {
                    if (!property.auction) return;
                    const url = `${getAnAuction}/${property.auction}`;
                    const response = await getData(url);
                    if (response.status === "Success") {
                        setAuction(response.data);
                        setIsAuction(true);
                    }
                } catch {
                    console.log("Error retrieving auction details");
                }
            };
            fetchAuction();
        } else {
            setIsLoading(true);
        }
    }, [getAnAuction, property, isPropertyFetched]);

    const images = property?.images?.length ? property.images : [];

    const handleNextImage = () => {
        const newIndex = (currentImageIndex + 1) % images.length;
        setCurrentImageIndex(newIndex);
        setMainImage(images[newIndex]);
    };

    const handlePrevImage = () => {
        const newIndex = (currentImageIndex - 1 + images.length) % images.length;
        setCurrentImageIndex(newIndex);
        setMainImage(images[newIndex]);
    };

    const handleImageChange = (clickedImage) => {
        setMainImage(clickedImage);
        setThumbnails((prev) =>
            prev.map((img) => (img === clickedImage ? mainImage : img))
        );
    };

    const handleBidSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        if (!isLoggedIn) return;
        try {
            const url = `${placeBid}/${property.auction}/placeBid`;
            const response = await postData(url, { bidAmount: parseInt(bid.bidAmount) });
            if (response.status === "Success") {
                setSuccessMessage(response.message);
                await getAllTheBids();
                setTimeout(() => setSuccessMessage(""), 2000);
            }
        } catch (error) {
            setErrorMessage(error.message);
            setTimeout(() => setErrorMessage(""), 2000);
        } finally {
            setIsLoading(false);
            setBidAmount('');
        }
    };

    const getAllTheBids = async () => {
        if (!isLoggedIn) return;
        try {
            const url = `${getBids}/${property.auction}/bids`;
            const response = await getData(url);
            if (response.status === "Success") {
                setPreviousBids(response.data.bids);
            }
        } catch {
            console.log("Error fetching bids");
        }
    };

    useEffect(() => {
        if (isPropertyFetched && property?.auction) {
            getAllTheBids();
        }
        // eslint-disable-next-line
    }, [isPropertyFetched]);

    const handleLogin = () => {
        navigate("/account/login", {
            state: { from: location.pathname }
        });
    }

    const handleChange = (event) => {
        setBidAmount({ bidAmount: parseInt(event.target.value) });
    };

    //Arrow visibility logic
    useEffect(() => {
        if (!isMobile) return;
      
        const handleScroll = () => {
          const ref = auctionRef.current;
          if (!ref) return;
      
          const rect = ref.getBoundingClientRect();
          const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      
          // Check if top of auction section is in view
          setShowScrollArrow(rect.top > windowHeight - 150); 
        };
      
        window.addEventListener("scroll", handleScroll);
        handleScroll(); // Call once on mount
      
        return () => window.removeEventListener("scroll", handleScroll);
    }, [isMobile]);

    const handleTouchStart = (e) => {
        setTouchStartX(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEndX(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStartX || !touchEndX) return;

        const distance = touchStartX - touchEndX;
        const isSwipe = Math.abs(distance) > 50;

        if (isSwipe) {
            if (distance > 0) {
                handleNextImage(); // swipe left
            } else {
                handlePrevImage(); // swipe right
            }
        }

        setTouchStartX(null);
        setTouchEndX(null);
    };
      
      
    
    

    return (
        <main className="placeBidWithPropertyDetail">
            {isLoading ? (
                <div className="loaderContainer">
                    <div className="loader"></div>
                    <p className="loadingText">Adding your bid/Loading...</p>
                </div>
            ) : (
                <>
                    {/* 🔽 Arrow Prompt */}
                    {isMobile && showScrollArrow && (
                        <div className="scrollArrow">
                            <span className="arrow-bounce">⬇</span>
                        </div>
                    )}

                    {/* Property Side */}
                    <div className="propertySide">
                        {/* Image Carousel */}
                        <div className="propertyImage">
                            <button className="imageNav left" onClick={handlePrevImage}>◀</button>
                            <div
                                className="propImage"
                                style={{
                                    backgroundImage: `url(${process.env.REACT_APP_BACKEND}/${mainImage?.replace('public/', '')})`,
                                }}
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                            ></div>
                            <button className="imageNav right" onClick={handleNextImage}>▶</button>
                        </div>

                        <div className="imageDots">
                            {thumbnails.map((_, index) => (
                                <span
                                    key={index}
                                    className={`dot ${mainImage === thumbnails[index] ? 'active' : ''}`}
                                ></span>
                            ))}
                        </div>


                        {/* Thumbnails */}
                        <div className="imageSlides">
                            {thumbnails.map((img, index) => (
                                <span
                                    key={index}
                                    className={`propImage${index + 1}`}
                                    style={{
                                        backgroundImage: `url(${process.env.REACT_APP_BACKEND}/${img.replace('public/', '')})`,
                                    }}
                                    onClick={() => handleImageChange(img)}
                                ></span>
                            ))}
                        </div>

                        {/* Details */}
                        <div className="listingDetail">
                            <div className="propertyPageTitle">
                                <h2 className="propertyTitle">{property?.title}</h2>
                                <div className="socialShareButtons" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                    <FacebookShareButton url={shareUrl} quote={shareTitle}><FacebookIcon size={32} round /></FacebookShareButton>
                                    <TwitterShareButton url={shareUrl} title={shareTitle}><TwitterIcon size={32} round /></TwitterShareButton>
                                    <WhatsappShareButton url={shareUrl} title={shareTitle}><WhatsappIcon size={32} round /></WhatsappShareButton>
                                    <LinkedinShareButton url={shareUrl} title={shareTitle}><LinkedinIcon size={32} round /></LinkedinShareButton>
                                </div>
                            </div>

                            <PriceProp
                                mainClassName="price bidPagePrice"
                                spanClassname1="propertyPrice bidPagePropertyPrice"
                                spanClassname2="favorite bidPageFavorite"
                                property={property}
                                isProperty={true}
                            />
                            <PropertyAddressProp
                                mainClassName="roomAddress bidPageAddress"
                                roomClassName="houseRoomsDetails bidPageHomeDetails"
                                addressClassName="propertyAddress bidPagePropertyAddress"
                                property={property}
                                isProperty={true}
                            />
                            <div className="detailsOfTheProperty">
                                <div className="propertyPageDescription">
                                    <h2 className="bidPageTitles">Description</h2>
                                    <PropertyDescriptionProp
                                        mainClassName="description bidPageDescription"
                                        textClassName="propertyDescription bidPagePropertyDescription"
                                        property={property}
                                        isMobile={isMobile}
                                        isProperty={true}
                                    />
                                </div>
                                <div className="propertyDisclaimer">
                                    <h2 className="bidPageTitles">Disclaimer</h2>
                                    <p>{property.disclaimer}</p>
                                </div>
                                <div className="propertySaleTerms">
                                    <h2 className="bidPageTitles">Terms of Sale</h2>
                                    <p>{property.termsForSale}</p>
                                </div>
                                <div className="propertyLegalTerm">
                                    <h2 className="bidPageTitles">Legal Terms</h2>
                                    <p>{property.legalTerms}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Auction Side */}
                    <div ref={auctionRef} className={`auctionSide ${!isAuction || auction?.status === 'scheduled' ? 'blurred' : ''}`}>
                        {isAuction ? (
                            <div className="placeBidCard">
                                <h2 className="auctionTitle">Auction Details</h2>
                                <div className="auctionRow">
                                    <div className="auctionContainer">
                                        <h3>Reserve Price</h3>
                                        <p>£{auction.reservePrice.toLocaleString()}</p>
                                    </div>
                                    <div className="auctionContainer">
                                        <h3>Min Increment</h3>
                                        <p>£{auction.increment.toLocaleString()}</p>
                                    </div>
                                </div>
                                {auction?.status !== 'closed' ? (
                                    <div className="auctionContainer placeBid">
                                        <h3 className="placeYourBid">Place Your Bid</h3>
                                        {isLoggedIn ? (
                                            <form onSubmit={handleBidSubmit} className="bidForm">
                                                <Input
                                                    type="number"
                                                    name="bidAmount"
                                                    value={bid.bidAmount}
                                                    onChange={handleChange}
                                                    placeholder="Enter bid amount"
                                                    required
                                                />
                                                <button type="submit" className="submitBid" disabled={userDetails.role === 'seller'}>
                                                    {userDetails.role === 'seller' ? 'You do not have permissions!' : 'Submit Bid'}
                                                </button>
                                                <div className="errorMesaage bidErrorMessage successBid">
                                                    <span className={`${errorMessage ? 'errorMessage' : 'successMessage'}`}>
                                                        {errorMessage || successMessage}
                                                    </span>
                                                </div>
                                            </form>
                                        ) : (
                                            <button onClick={handleLogin} className="loginPlaceBId">Log in to place a bid</button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="auctionContainer placeBid">
                                        <h3 className="placeYourBid auctionNowClosed">Auction is now closed!</h3>
                                    </div>
                                )}
                                <div className={`auctionContainer leaderboard ${!isLoggedIn ? 'lockedLeaderboard' : ''}`}>
                                    <h3>Top Bidders</h3>
                                    <ul>
                                        {previousBids.length > 0 ? (
                                            previousBids
                                                .sort((a, b) => b.bidAmount - a.bidAmount)
                                                .slice(0, 3)
                                                .map((bidder, index) => {
                                                    let username = bidder.user.username;
                                                    if (username.length > 8) username = username.slice(0, 8);
                                                    const maskLength = Math.max(0, username.length - 1);

                                                    const maskedUsername = username.slice(0, 1) + '*'.repeat(maskLength);
                                                    return (
                                                        <li key={index}>
                                                            <img
                                                                alt="Profile"
                                                                src={`${process.env.REACT_APP_BACKEND}/${bidder.user.image.replace('public/', '')}`}
                                                                className="userProfile"
                                                            />
                                                            <span className="bidderName">{maskedUsername}</span>
                                                            <span className="bidderAmount">£{bidder.bidAmount.toLocaleString()}</span>
                                                        </li>
                                                    );
                                                })
                                        ) : (
                                            <p>Be the first one to place a bid!</p>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        ) : (
                            <p>Loading...</p>
                        )}
                    </div>
                </>
            )}
        </main>
    );
}
